import {
  Typography,
  createStyles,
  makeStyles,
  Theme,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  OutlineButton,
} from '@bb-ui/react-library';
import { Link } from 'react-router-dom';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { ErrorMessage } from 'components/ErrorMessage';
import useRestApi from 'hooks/useRestApi';
import axios from 'axios';
import { apiUrl } from 'utils/apiUrl';
import { useTenantContext } from 'contexts/TenantContext';
import { useAuthContext } from 'contexts/AuthContext';
import { Close } from '@bb-ui/icons/dist/small';

const LooseIconButton = IconButton as any;

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    section: {
      marginBottom: theme.spacing(3),
    },
    header: {
      marginBottom: theme.spacing(2),
    },
    searchInput: {
      marginBottom: theme.spacing(2),
      padding: theme.spacing(1, 1.5),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      fontSize: '14px',
      width: '240px',
      outline: 'none',
    },
    cardList: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      overflow: 'hidden',
    },
    card: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1.5, 2),
      backgroundColor: theme.palette.background.paper,
      gap: theme.spacing(2),
      borderBottom: `1px solid ${theme.palette.divider}`,
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    cardName: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      minWidth: 280,
    },
    awsBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#FF9900',
      color: '#ffffff',
      fontSize: '10px',
      fontWeight: 700,
      padding: '2px 6px',
      borderRadius: '3px',
      letterSpacing: '0.5px',
    },
    cardMeta: {
      flex: 1,
      display: 'flex',
      gap: theme.spacing(4),
    },
    metaText: {
      fontSize: '13px',
      color: theme.palette.text.secondary,
    },
    instancesButton: {
      marginLeft: 'auto',
      whiteSpace: 'nowrap',
    },
    noDataBox: {
      padding: theme.spacing(3),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.background.paper,
    },
    // Drawer styles
    drawerPaper: {
      padding: theme.spacing(2),
      minWidth: 280,
    },
    drawerCloseButton: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    drawerTitle: {
      fontWeight: 'bold',
      marginBottom: theme.spacing(1),
      paddingLeft: theme.spacing(2),
    },
    drawerTotalSites: {
      paddingLeft: theme.spacing(2),
      paddingBottom: theme.spacing(1),
      fontSize: '13px',
      color: theme.palette.text.secondary,
    },
    drawerUpdateButton: {
      margin: theme.spacing(2),
    },
    instanceLink: {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      fontSize: '14px',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    instanceLinkButton: {
      width: '100%',
      borderRadius: theme.shape.borderRadius,
      justifyContent: 'flex-start',
      textAlign: 'left',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.06)',
      },
    },
    drawerListItem: {
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
    },
  }),
);

interface SharedRdsItem {
  fleetId?: string;
  databaseId?: string;
  secretsArn?: string;
  allocatedStorage?: number;
  instanceType?: string;
  learnInstanceIds?: string[];
}

export const SharedRds: React.FunctionComponent = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { tenantId, tenant } = useTenantContext();
  const { idToken } = useAuthContext();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState<SharedRdsItem | null>(null);
  const [learnNameMap, setLearnNameMap] = React.useState<Record<string, string>>({});

  const fetchedIdsRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    if (!selectedItem?.learnInstanceIds?.length) return;

    const headers = { Authorization: `Bearer ${idToken}` };

    selectedItem.learnInstanceIds.forEach((id) => {
      if (fetchedIdsRef.current.has(id)) return;
      fetchedIdsRef.current.add(id);

      axios
        .get(apiUrl('lct', `tenants/${id}/instances/learnInstances`), { headers })
        .then(({ data }) => {
          if (data?.Name) {
            setLearnNameMap((prev) => ({ ...prev, [id]: data.Name }));
          }
        })
        .catch(() => {});
    });
  }, [selectedItem, idToken]);

  const {
    data: sharedRdsData,
    loading: dataLoading,
    error: dataError,
  } = useRestApi(apiUrl('lct', `tenants/${tenantId}/databases`));

  const dataItems = React.useMemo<SharedRdsItem[]>(
    () => sharedRdsData?.items ?? [],
    [sharedRdsData],
  );

  const filteredItems = React.useMemo(
    () =>
      dataItems.filter(
        (item) =>
          !searchQuery || item.databaseId?.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [dataItems, searchQuery],
  );

  const handleOpenDrawer = (item: SharedRdsItem) => {
    setSelectedItem(item);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedItem(null);
  };

  const instanceCount = selectedItem?.learnInstanceIds?.length ?? 0;
  const domain =
    tenant?.region === 'us-gov-west-1'
      ? 'https://console.amazonaws-us-gov.com'
      : 'https://console.aws.amazon.com';

  let content: React.ReactElement;

  if (dataError) {
    content = (
      <ErrorMessage
        data-testid="shared-rds-error"
        title={t('sharedRds.loadError')}
        message={dataError.message}
        variant="block"
      />
    );
  } else if (dataLoading) {
    content = <LoadingIndicator data-testid="shared-rds-loading" />;
  } else if (dataItems.length === 0) {
    content = (
      <div className={classes.noDataBox} data-testid="shared-rds-no-data">
        <Typography variant="body2" color="textSecondary">
          {t('sharedRds.noData')}
        </Typography>
      </div>
    );
  } else {
    content = (
      <div className={classes.cardList}>
        {filteredItems.map((item) => {
          const count = item.learnInstanceIds?.length ?? 0;
          const instanceLabel = `${count} / 10 ${t('sharedRds.learnSites')}`;
          return (
            <div key={item.databaseId} className={classes.card}>
              <div className={classes.cardName}>
                <Typography variant="body2">{item.databaseId}</Typography>
                {tenant?.region && (
                  <a
                    href={`${domain}/rds/home?region=${tenant?.region}#database:id=${item.databaseId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    <span className={classes.awsBadge}>AWS</span>
                  </a>
                )}
              </div>
              <div className={classes.cardMeta}>
                <Typography className={classes.metaText}>
                  {t('sharedRds.allocatedStorage')}: {item.allocatedStorage ?? '—'}
                </Typography>
                <Typography className={classes.metaText}>
                  {t('sharedRds.instanceType')}: {item.instanceType ?? '—'}
                </Typography>
              </div>
              <OutlineButton
                className={classes.instancesButton}
                onClick={() => handleOpenDrawer(item)}
                data-testid={`learn-instances-button-${item.databaseId}`}
              >
                {instanceLabel}
              </OutlineButton>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div data-testid="shared-rds-page">
      <input
        className={classes.searchInput}
        placeholder={t('sharedRds.search', 'Search Shared RDS')}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        data-testid="shared-rds-search"
      />

      <div className={classes.section}>{content}</div>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        classes={{ paper: classes.drawerPaper }}
      >
        <div className={classes.drawerCloseButton}>
          <IconButton onClick={handleCloseDrawer} aria-label="Close">
            <Close />
          </IconButton>
        </div>

        <Typography variant="h4" className={classes.drawerTitle}>
          {t('sharedRds.learnSitesTitle')} {selectedItem?.databaseId}:
        </Typography>

        <List>
          {(selectedItem?.learnInstanceIds ?? []).map((learnTenantId) => (
            <ListItem key={learnTenantId} className={classes.drawerListItem}>
              <LooseIconButton
                aria-label={`View Learn Instance ${learnTenantId}`}
                component={Link}
                to={`/learn-site/${learnTenantId}/information`}
                className={classes.instanceLinkButton}
              >
                <ListItemText primary={learnNameMap[learnTenantId] || learnTenantId} />
              </LooseIconButton>
            </ListItem>
          ))}
        </List>

        <Divider />

        <Typography className={classes.drawerTotalSites}>
          {t('sharedRds.drawer.totalSites', 'Total Sites')}: {`${instanceCount} / 10`}
        </Typography>
      </Drawer>
    </div>
  );
};
